import React, { useCallback, useEffect, useMemo, useReducer } from 'react'
import { useRouter } from 'next/router'
import qs from 'query-string'

import useRequest from '../../hooks/useRequest'
import { THEME, COLORS } from '../../styles'
import { pluralize } from '../../helpers'
import { useTranslation } from '../../contexts/I18nContext'
import RatingStars from '../RatingStars'
import Drawer from '../Drawer'
import RecipeFeedbackForm from '../RecipeFeedbackForm'
import { KEY_VALUES } from '../../constants'

import Styled from './RecipeFeedback.styled'

const FEEDBACK_COUNT_MINIMUM = 5
const FEEDBACKS_TO_FETCH = 10

const RecipeFeedback = ({
  aggregateRating,
  auth,
  className,
  config,
  editRatedRecipes,
  isRecipeAuthor,
  openModal,
  profile,
  readOnly,
  recipeAuthor,
  recipeId,
  recipeImageUrl,
  recipeSource,
  recipeTitle,
  showError,
  totalFeedbacks,
}) => {
  const router = useRouter()
  const request = useRequest()
  const { t } = useTranslation()

  const [state, setState] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      canSubmit: false,
      comments: '',
      count: 10,
      feedbacks: [],
      hasMore: false,
      isLoading: true,
      userLikes: [],
      n_rating: undefined,
      options: [],
      start: 0,
      thanks: false,
      writing: false,
    },
  )

  const isUserGeneratedRecipe = recipeSource === 'user-generated'

  const getFeedbacks = useCallback(
    ({ start = 0, count = 10 } = {}) => {
      if (recipeId) {
        setState({ isLoading: true, start, count })
        request({
          name: 'getRecipeFeedbacks',
          delay: 1500,
          recipeId,
          chronologicalSort: 'desc',
          allowEmptyComments: false,
          start,
          count,
          source: recipeSource,
          accessToken: auth.accessToken,
          availabilityZoneId: process.env.NEXT_PUBLIC_AVAILABILITY_ZONE,
        })
          .then((response = []) => {
            const feedbacks =
              start === 0 ? response : (state.feedbacks || []).concat(response)
            setState({
              feedbacks,
              isLoading: false,
              hasMore: feedbacks.length === start + count,
              userLikes: feedbacks
                .filter(
                  (feedback) =>
                    feedback?.isLikedByUser || feedback?.likedByUser,
                )
                .map(({ _id }) => _id),
            })
          })
          .catch(() => {
            setState({ feedbacks: [], isLoading: false, hasMore: false })
          })
      }
    },
    [recipeId, request, recipeSource, auth.accessToken, state.feedbacks],
  )

  useEffect(() => {
    getFeedbacks({ start: 0, count: FEEDBACKS_TO_FETCH })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.accessToken])

  const getMoreFeedback = useCallback(() => {
    if (!state.isLoading) {
      getFeedbacks({
        count: state.count,
        start: state.start + FEEDBACKS_TO_FETCH,
      })
    }
  }, [getFeedbacks, state.count, state.isLoading, state.start])

  const handleOpen = useCallback(() => {
    if (auth.anonymous) {
      openModal('Login', { isDiscoveryMode: true })
    } else if (!state.writing && !state.thanks) {
      setState({
        writing: true,
        subject: isUserGeneratedRecipe ? 'rating' : '',
      })
    }
  }, [
    state.writing,
    state.thanks,
    auth.anonymous,
    openModal,
    isUserGeneratedRecipe,
  ])

  const handleSubmit = useCallback(() => {
    if (state.n_rating > 0) {
      request({
        name: 'setRecipeFeedback',
        accessToken: auth.accessToken,
        recipeId,
        body: {
          n_rating: state.n_rating,
          options: state.options || [],
          comments: state.comments,
          source: recipeSource,
        },
      })
        .then(() => {
          editRatedRecipes(recipeId)
          showError({ code: 'FEEDBACK_SENT' })
          setState({
            writing: false,
            thanks: true,
            isLoading: true,
            comments: '',
            options: [],
            n_rating: undefined,
          })
          getFeedbacks({ start: 0, count: FEEDBACKS_TO_FETCH })
        })
        .catch((error) => showError(error))
    }
  }, [
    auth.accessToken,
    editRatedRecipes,
    getFeedbacks,
    recipeId,
    recipeSource,
    request,
    showError,
    state.comments,
    state.n_rating,
    state.options,
  ])

  const ratingStars = useMemo(
    () => (aggregateRating ? (aggregateRating * 5).toFixed(2) : 0),
    [aggregateRating],
  )

  useEffect(() => {
    if (router.isReady) {
      setState({
        canSubmit:
          !auth.anonymous && auth.accessToken && !readOnly && !isRecipeAuthor,
      })
    }
  }, [
    auth.accessToken,
    auth.anonymous,
    isRecipeAuthor,
    readOnly,
    router.isReady,
  ])

  const handleReport = useCallback(
    (feedback) => {
      openModal('ReportOrBlockContent', {
        source: 'feedback',
        recipe: { id: recipeId, source: recipeSource },
        feedback: { id: feedback?._id, comments: feedback?.comments },
        user: { id: feedback?.authorId, name: feedback?.author },
      })
    },
    [openModal, recipeId, recipeSource],
  )

  const handleDelete = useCallback(
    (feedback) => {
      if (feedback?.isFeedbackAuthor) {
        showError({
          code: 'DELETE_FEEDBACK',
          actions: [
            {
              text: t('DELETE'),
              onClick: () => {
                const feedbacks = state.feedbacks
                setState({
                  feedbacks: feedbacks.filter((f) => f?._id !== feedback?._id),
                })
                request({
                  name: 'deleteUserRecipeFeedback',
                  accessToken: auth.accessToken,
                  recipeId,
                  body: {
                    feedbackId: feedback?._id,
                  },
                })
                  .then(() => {
                    editRatedRecipes(recipeId)
                  })
                  .catch(() => {
                    setState({ feedbacks })
                  })
              },
            },
            { text: t('CANCEL') },
          ],
        })
      }
    },
    [
      auth.accessToken,
      editRatedRecipes,
      recipeId,
      request,
      showError,
      state.feedbacks,
      t,
    ],
  )

  const handleLike = useCallback(
    (feedback) => {
      if (!feedback?.isFeedbackAuthor && auth.accessToken && !auth.anonymous) {
        const userLikes = state.userLikes
        const feedbacks = state.feedbacks
        const isLikedByUser = userLikes.includes(feedback._id)
        if (isLikedByUser) {
          setState({
            userLikes: userLikes.filter((id) => id !== feedback?._id),
            feedbacks: feedbacks.reduce((updated, current) => {
              if (current?._id === feedback._id) {
                return updated.concat({
                  ...current,
                  likes: (current.likes || 0) - 1,
                })
              }
              return updated.concat(current)
            }, []),
          })
        } else {
          setState({
            userLikes: userLikes.concat(feedback?._id),
            feedbacks: feedbacks.reduce((updated, current) => {
              if (current?._id === feedback._id) {
                return updated.concat({
                  ...current,
                  likes: (current.likes || 0) + 1,
                })
              }
              return updated.concat(current)
            }, []),
          })
        }
        request({
          name: isLikedByUser ? 'dislikeRecipeFeedback' : 'likeRecipeFeedback',
          accessToken: auth.accessToken,
          feedbackId: feedback?._id,
        }).catch(() => {
          setState({ feedbacks, userLikes })
        })
      }
    },
    [
      auth.accessToken,
      auth.anonymous,
      request,
      state.feedbacks,
      state.userLikes,
    ],
  )

  useEffect(() => {
    if (state.canSubmit && !state.writing && router.query?.review === 'true') {
      setState({ writing: true })
      router.replace(
        qs.stringifyUrl(
          {
            url: router.pathname,
            query: { ...router.query, review: '' },
          },
          { skipEmptyString: true },
        ),
        undefined,
        { shallow: true },
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath, router.query, state.canSubmit, state.writing])

  return (
    <Styled className={className} data-scroll-offset id="feedbacks">
      <Styled.headline>
        {totalFeedbacks >= FEEDBACK_COUNT_MINIMUM ? (
          <>
            <RatingStars rating={ratingStars} size={18} id="recipe-feedback" />
            <Styled.count>
              {pluralize(t('RECIPE.FEEDBACK'), totalFeedbacks, {
                plural: t('RECIPE.FEEDBACKS'),
              })}
            </Styled.count>
          </>
        ) : (
          <Styled.noCount>{t('RECIPE.FEEDBACKS')}</Styled.noCount>
        )}
        {!state.thanks && !state.writing ? (
          <Styled.writeCommentButton onClick={handleOpen}>
            <Styled.writeCommentButtonIcon
              name="pen-pen-16"
              fill={COLORS.black}
              width={16}
            />
            <Styled.writeCommentButtonText>
              {t('RECIPE.FEEDBACK_BUTTON')}
            </Styled.writeCommentButtonText>
          </Styled.writeCommentButton>
        ) : null}

        {state.thanks ? (
          <Styled.thanks>
            <Styled.thanksText>{t('THANKS')}</Styled.thanksText>
            <Styled.thanksIcon
              name="checkmark-16"
              fill={THEME.COLORS.semantic.success}
              width={16}
            />
          </Styled.thanks>
        ) : null}
      </Styled.headline>

      {state.feedbacks?.length > 0 ? (
        <>
          <Styled.comments>
            {state.feedbacks.map((feedback) => {
              const isAuthorBlocked = (
                profile?.blockedEntities?.user || []
              ).includes(feedback.authorId)
              const isFeedbackBlocked = (
                profile?.blockedEntities?.feedback || []
              ).includes(feedback._id)
              return (
                <Styled.comment
                  key={feedback._id}
                  canReport={state.canSubmit}
                  feedback={feedback}
                  isAuthorBlocked={isAuthorBlocked}
                  isFeedbackAuthor={feedback.isFeedbackAuthor}
                  isLikedByUser={state.userLikes.includes(feedback._id)}
                  likes={feedback.likes}
                  isFeedbackBlocked={isFeedbackBlocked}
                  recipeAuthor={recipeAuthor}
                  onReportClick={() => handleReport(feedback)}
                  onDeleteClick={() => handleDelete(feedback)}
                  onLikeClick={() => handleLike(feedback)}
                />
              )
            })}
          </Styled.comments>
          {state.hasMore ? (
            <Styled.seeMoreFooter>
              <Styled.seeMoreButton
                role="button"
                tabIndex={state.isLoading ? -1 : 0}
                onClick={getMoreFeedback}
                onKeyPress={(event) => {
                  if (event.key === KEY_VALUES.ENTER) {
                    getMoreFeedback()
                  }
                }}
                isLoading={state.isLoading}
              >
                <Styled.seeMoreLabel isLoading={state.isLoading}>
                  {t('RECIPE.SEE_MORE_FEEDBACKS')}
                </Styled.seeMoreLabel>
                {state.isLoading ? (
                  <Styled.seeMoreLoading fill={COLORS.black} width={16} />
                ) : (
                  <Styled.seeMoreIcon
                    name="direction-down-16"
                    width={16}
                    height={16}
                    fill={COLORS.black}
                  />
                )}
              </Styled.seeMoreButton>
            </Styled.seeMoreFooter>
          ) : null}
        </>
      ) : (
        <Styled.comments>
          <Styled.noComment>
            {state.isLoading ? (
              <Styled.noCommentLoading
                width={16}
                fill={THEME.COLORS.foreground.secondary}
              />
            ) : null}
            <Styled.noCommentLabel>
              {state.isLoading
                ? t('RECIPE.RETRIEVING_REVIEWS')
                : state.canSubmit
                  ? t('RECIPE.NO_REVIEW_GIVE_YOURS')
                  : t('RECIPE.NO_REVIEW')}
            </Styled.noCommentLabel>
          </Styled.noComment>
        </Styled.comments>
      )}

      {state.canSubmit ? (
        <>
          <Drawer
            isOpened={state.writing}
            onClose={() => setState({ writing: false })}
            drawerId="recipe-feedback"
            maxWidth={560}
          >
            {!state.thanks ? (
              <RecipeFeedbackForm
                author={recipeAuthor}
                title={recipeTitle}
                source={recipeSource}
                imageUrl={recipeImageUrl}
                options={config.feedbackOptions}
                setValue={setState}
                value={{
                  options: state.options,
                  comments: state.comments,
                  n_rating: state.n_rating,
                }}
                canSubmit={state.n_rating > 0 && state.n_rating <= 5}
                onSubmit={handleSubmit}
              />
            ) : null}
          </Drawer>
        </>
      ) : null}
    </Styled>
  )
}

RecipeFeedback.defaultProps = {
  isRecipeAuthor: false,
  readOnly: false,
  recipeSource: 'jow',
  aggregateRating: 0,
  totalFeedbacks: 0,
}

export default RecipeFeedback
